import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import NotFound from "@/pages/NotFound";
import { ArrowDownRight } from "lucide-react";

export default function CmsPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPage(null);
    setNotFound(false);
    api
      .get(`/nav/page/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <NotFound />;
  if (!page) return <div className="min-h-screen bg-[#050505]" />;

  const c = page.content || {};
  const isInternal = (c.button_link || "").startsWith("/");

  return (
    <section data-testid="cms-page" className="relative z-10 bg-[#050505] py-16 lg:py-24">
      <Seo title={`G-Repair — ${c.title}`} description={c.intro} />
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <h1 data-testid="cms-page-title" className="font-display text-white text-4xl sm:text-5xl leading-[1.05] mb-6">
          {c.title}
        </h1>
        {c.intro && (
          <p data-testid="cms-page-intro" className="text-[#999] text-base sm:text-lg leading-relaxed mb-12 max-w-2xl">
            {c.intro}
          </p>
        )}

        <div className="space-y-14">
          {(c.sections || []).map((s, i) => (
            <div key={i} data-testid={`cms-section-${i}`}>
              {s.image_path && (
                <div className="rounded-3xl border border-white/10 overflow-hidden mb-6 bg-[#0a0a0a]">
                  <img src={`/api/nav/files/${s.image_path}`} alt={s.heading || ""} className="w-full max-h-[420px] object-cover" />
                </div>
              )}
              {s.heading && <h2 className="font-display text-2xl sm:text-3xl text-white mb-3">{s.heading}</h2>}
              {s.text && <p className="text-[#aaa] leading-relaxed whitespace-pre-line">{s.text}</p>}
            </div>
          ))}
        </div>

        {c.button_label && c.button_link && (
          <div className="mt-14">
            {isInternal ? (
              <Link
                to={c.button_link}
                data-testid="cms-page-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-white text-[#050505] pl-7 pr-2 py-2 text-base font-medium hover:bg-[#d1d5db] transition-colors duration-300"
              >
                {c.button_label}
                <span className="w-11 h-11 rounded-full bg-[#050505] text-white flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                  <ArrowDownRight size={18} />
                </span>
              </Link>
            ) : (
              <a
                href={c.button_link}
                target="_blank"
                rel="noreferrer"
                data-testid="cms-page-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-white text-[#050505] pl-7 pr-2 py-2 text-base font-medium hover:bg-[#d1d5db] transition-colors duration-300"
              >
                {c.button_label}
                <span className="w-11 h-11 rounded-full bg-[#050505] text-white flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                  <ArrowDownRight size={18} />
                </span>
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
